import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useRolesActions from '../../../hooks/APIHooks/users/useRolesActions';
import { Role, RoleReqBody } from '../../../types/roles';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditRoleModal = ({
  title,
  role,
  handleModalClose,
}: {
  title: 'create' | 'update';
  role?: Role;
  handleModalClose: () => void;
}) => {
  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { createNewRoleHandler, updateRoleHandler } = useRolesActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const createEditRoleHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log('ROLE REQ VALUES: ', values);

      let body: RoleReqBody = {
        role_name: values.role_name,
        description: values.description,
      };

      if (role) {
        body = {
          ...body,
          updated_by: currentUser?.username,
        };

        await updateRoleHandler
          .mutateAsync({ roleId: role.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Role Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['role-list'] });
            } else {
              toast.error(res.message || 'Role Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = {
          ...body,
          created_by: currentUser?.username,
        };

        await createNewRoleHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Role Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['role-list'] });
            } else {
              toast.error(res.message || 'Role Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit role modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} role`} />

      <Formik
        initialValues={{
          role_name: role?.role_name || '',
          description: role?.description || '',
        }}
        onSubmit={createEditRoleHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaInputField
              type='text'
              name='role_name'
              label='Role Name'
              placeholder='Enter role name here...'
              isRequired
            />

            <AcademiaTextAreaField
              name='description'
              label='Role Description'
              placeholder='Enter role description here...'
            />

            <AcademiaButton
              type='submit'
              variant='premium'
              isLoading={props.isSubmitting}
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              fullWidth
            >
              {title} Role
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditRoleModal;
